import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface CtaSecondaryLegacy {
  text: string;
  link: string;
}

interface CtaNew {
  text: string;
  href?: string;
  icon?: string;
  action?: () => void;
}

interface StatItem {
  value: string;
  label: string;
}

interface PageHeroProps {
  badge: string;
  title: string;
  titleHighlight?: string;
  titleHighlightColor?: 'green' | 'orange';
  /** @deprecated use titleHighlight */
  highlightedWord?: string;
  /** @deprecated use titleHighlightColor */
  highlightColor?: 'green' | 'orange';
  subtitle: string;
  benefits?: string[];
  /** @deprecated use primaryCta */
  ctaText?: string;
  /** @deprecated use primaryCta */
  ctaLink?: string;
  /** @deprecated use secondaryCta */
  ctaSecondary?: CtaSecondaryLegacy;
  primaryCta?: CtaNew;
  secondaryCta?: CtaNew;
  backgroundImage: string;
  overlayOpacity?: 'light' | 'medium' | 'dark';
  overlayIntensity?: 'light' | 'medium' | 'dark';
  stats?: StatItem[];
}

const overlayClasses = {
  light: 'bg-gradient-to-b from-black/40 via-black/30 to-black/40',
  medium: 'bg-gradient-to-b from-black/60 via-black/50 to-black/60',
  dark: 'bg-gradient-to-b from-black/75 via-black/65 to-black/75',
};

const highlightColorClasses = {
  green: 'text-green-400',
  orange: 'text-orange-400',
};

export default function PageHero({
  badge,
  title,
  titleHighlight,
  titleHighlightColor = 'orange',
  highlightedWord,
  highlightColor = 'green',
  subtitle,
  benefits,
  ctaText,
  ctaLink,
  ctaSecondary,
  primaryCta,
  secondaryCta,
  backgroundImage,
  overlayOpacity,
  overlayIntensity,
  stats,
}: PageHeroProps) {
  const { ref, isVisible } = useScrollAnimation();

  // Normaliza overlay
  const overlay = overlayIntensity ?? overlayOpacity ?? 'medium';

  // Normaliza highlight
  const highlight = titleHighlight ?? highlightedWord;
  const highlightColor_ = titleHighlightColor ?? highlightColor ?? 'orange';

  // Normaliza CTAs
  const resolvedPrimary: CtaNew | null = primaryCta ?? (ctaText ? { text: ctaText, href: ctaLink } : null);
  const resolvedSecondary: CtaNew | null =
    secondaryCta ??
    (ctaSecondary ? { text: ctaSecondary.text, href: ctaSecondary.link } : null);

  const renderTitle = () => {
    if (!highlight) {
      return (
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight text-center">
          {title}
        </h1>
      );
    }

    // Suporte a **palavra** no título (formato markdown bold)
    const boldRegex = /\*\*(.+?)\*\*/g;
    if (boldRegex.test(title)) {
      const parts = title.split(/\*\*(.+?)\*\*/g);
      return (
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight text-center">
          {parts.map((part, i) =>
            i % 2 === 1 ? (
              <span key={i} className={highlightColorClasses[highlightColor_]}>
                {part}
              </span>
            ) : (
              part
            )
          )}
        </h1>
      );
    }

    const parts = title.split(highlight);
    return (
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight text-center">
        {parts[0]}
        <span className={highlightColorClasses[highlightColor_]}>{highlight}</span>
        {parts[1]}
      </h1>
    );
  };

  const renderCta = (cta: CtaNew, isPrimary: boolean) => {
    const baseClass = isPrimary
      ? 'inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer'
      : 'inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer';

    const icon = cta.icon ?? (isPrimary ? 'ri-mail-send-line' : 'ri-whatsapp-line');

    if (cta.action) {
      return (
        <button type="button" onClick={cta.action} className={baseClass}>
          <i className={`${icon} text-xl`}></i>
          {cta.text}
        </button>
      );
    }

    const href = cta.href ?? '#';
    const isExternal = href.startsWith('http');

    return (
      <a
        href={href}
        className={baseClass}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'nofollow noreferrer' : undefined}
      >
        <i className={`${icon} text-xl`}></i>
        {cta.text}
      </a>
    );
  };

  return (
    <section
      className="relative w-full min-h-[600px] lg:min-h-[700px] flex items-center bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {/* Overlay */}
      <div className={`absolute inset-0 ${overlayClasses[overlay]}`}></div>

      {/* Conteúdo */}
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={`relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-6">
            <i className="ri-shield-check-line text-green-400"></i>
            <span className="text-sm font-medium text-white uppercase tracking-wide">{badge}</span>
          </div>

          {/* Título */}
          {renderTitle()}

          {/* Subtítulo */}
          <p className="text-lg sm:text-xl text-gray-100 mb-8 leading-relaxed max-w-3xl mx-auto">
            {subtitle}
          </p>

          {/* Benefícios */}
          {benefits && benefits.length > 0 && (
            <ul className="space-y-3 mb-8 max-w-2xl mx-auto">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-3 justify-center text-left">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
                  </div>
                  <span className="text-base sm:text-lg text-gray-100">{benefit}</span>
                </li>
              ))}
            </ul>
          )}

          {/* CTAs */}
          {(resolvedPrimary || resolvedSecondary) && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {resolvedPrimary && renderCta(resolvedPrimary, true)}
              {resolvedSecondary && renderCta(resolvedSecondary, false)}
            </div>
          )}

          {/* Stats */}
          {stats && stats.length > 0 && (
            <div className="mt-12 flex flex-wrap gap-8 justify-center">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-sm text-gray-300 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
